import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import './FieldModuleOnboardingCallout.style.scss'

const CALLOUT_GAP_PX = 14

const FieldModuleOnboardingCallout = ({anchorId, onDismiss}) => {
  const {t} = useTranslation()
  const calloutRef = useRef(null)
  const [position, setPosition] = useState(null)

  const updatePosition = useCallback(() => {
    const anchor = document.getElementById(anchorId)
    const callout = calloutRef.current

    if (!anchor || !callout) {
      return
    }

    const anchorRect = anchor.getBoundingClientRect()
    const calloutRect = callout.getBoundingClientRect()
    const top = anchorRect.top + anchorRect.height / 2 - calloutRect.height / 2
    const left = anchorRect.left - calloutRect.width - CALLOUT_GAP_PX

    setPosition({
      top: Math.max(12, Math.min(top, window.innerHeight - calloutRect.height - 12)),
      left: Math.max(12, left),
    })
  }, [anchorId])

  useLayoutEffect(() => {
    updatePosition()
  }, [updatePosition])

  useEffect(() => {
    const handleLayoutChange = () => {
      updatePosition()
    }

    window.addEventListener('resize', handleLayoutChange)
    window.addEventListener('scroll', handleLayoutChange, true)

    const anchor = document.getElementById(anchorId)
    const resizeObserver = anchor && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(handleLayoutChange)
      : null

    if (anchor && resizeObserver) {
      resizeObserver.observe(anchor)
    }

    const retryTimer = window.setInterval(() => {
      if (document.getElementById(anchorId)) {
        updatePosition()
      }
    }, 200)

    return () => {
      window.clearInterval(retryTimer)
      window.removeEventListener('resize', handleLayoutChange)
      window.removeEventListener('scroll', handleLayoutChange, true)
      resizeObserver?.disconnect()
    }
  }, [anchorId, updatePosition])

  if (!position) {
    return (
      <div
        ref={calloutRef}
        className='gis-field-onboarding'
        style={{visibility: 'hidden', position: 'fixed', top: 0, left: 0}}
        aria-hidden='true'
      >
        <div className='gis-field-onboarding__bubble'>
          <p className='gis-field-onboarding__title'>{t('fields:onboarding.title')}</p>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={calloutRef}
      className='gis-field-onboarding'
      style={{top: `${position.top}px`, left: `${position.left}px`}}
      role='dialog'
      aria-labelledby='gis-field-onboarding-title'
    >
      <div className='gis-field-onboarding__bubble'>
        <p id='gis-field-onboarding-title' className='gis-field-onboarding__title'>
          {t('fields:onboarding.title')}
        </p>
        <p className='gis-field-onboarding__text'>{t('fields:onboarding.description')}</p>
        <button type='button' className='btn btn-sm btn-primary' onClick={onDismiss}>
          {t('fields:onboarding.dismiss')}
        </button>
      </div>
      <span className='gis-field-onboarding__arrow' aria-hidden='true' />
    </div>
  )
}

export default FieldModuleOnboardingCallout
